import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

// "How to use HomePlated" one-pager, one baked image per language. Spanish
// variants (es-MX) get the Spanish image; everything else falls back to English.
const INSTRUCTIONS_IMAGE = {
  en: '/images/HP%20Instructions%20English.jpeg',
  es: '/images/HP%20Instructions%20Spanish.jpeg',
} as const;

interface PublishInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Full-screen image overlay rather than the shared Modal card: the poster is
// dense text, and the card's padding and max width shrink it below legible on
// phones. Portaled to body with `hp-overlay-layer` so it also pins to the
// desktop iPhone frame, same as Modal.
export const PublishInstructionsModal: React.FC<PublishInstructionsModalProps> = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation('publish');
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();
  const src = lang.startsWith('es') ? INSTRUCTIONS_IMAGE.es : INSTRUCTIONS_IMAGE.en;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="hp-overlay-layer fixed inset-0 z-[60] flex flex-col bg-black/60"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      onClick={onClose}
    >
      <div className="flex justify-end px-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-10 w-10 items-center justify-center rounded-full text-white hover:bg-white/10 active:scale-95 transition"
        >
          <IonIcon icon={closeOutline} className="text-3xl" />
        </button>
      </div>

      {/* min-h-0 lets the image shrink to the space left by the close row and
          button; object-contain keeps the whole poster visible at max size.
          The app viewport disables page zoom, so pinch / double-tap zoom on the
          poster is handled here. Clicks are stopped at this area so releasing a
          pan over the backdrop doesn't dismiss the overlay. The library's state
          resets on its own, since the overlay unmounts when closed. */}
      <div className="min-h-0 flex-1 px-2" onClick={(e) => e.stopPropagation()}>
        <TransformWrapper minScale={1} maxScale={4} doubleClick={{ mode: 'toggle', step: 1.5 }}>
          <TransformComponent
            wrapperStyle={{ width: '100%', height: '100%' }}
            contentStyle={{ width: '100%', height: '100%' }}
            contentClass="flex items-center justify-center"
          >
            <img
              src={src}
              alt={t('instructionsPopup.alt')}
              className="max-h-full max-w-full object-contain"
            />
          </TransformComponent>
        </TransformWrapper>
      </div>

      <div className="px-4 pb-4 pt-3">
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-full bg-[var(--ion-color-primary)] px-5 py-3 text-base font-semibold text-white shadow-sm active:scale-[0.99] transition-transform"
        >
          {t('instructionsPopup.gotIt')}
        </button>
      </div>
    </div>,
    document.body,
  );
};
